import {
  AttachmentQueue,
  AttachmentState,
  type AttachmentQueueOptions,
} from "@powersync/web"
import { AUTOMATIC_RETRY_MARKER_PREFIX } from "./attachmentFailureQueries"
import { requeueMissingDownloads } from "./requeueMissingDownloads"

const LEGACY_RESTORATION_MARKER = "legacy-failure-restoration-v1"

/**
 * The SDK evicts every archived row, including never-uploaded files. Disable
 * that cleanup and apply the cache limit only to files with a server copy.
 * Explicit deleteFile/clearQueue operations still delete files as requested.
 */
export class UploadSafeAttachmentQueue extends AttachmentQueue {
  private readonly syncedArchiveLimit: number

  constructor(options: AttachmentQueueOptions) {
    super({ ...options, archivedCacheLimit: Number.MAX_SAFE_INTEGER })
    this.syncedArchiveLimit = options.archivedCacheLimit ?? 500
  }

  override async startSync(): Promise<void> {
    await this.restoreLegacyFailures()
    await super.startSync()
  }

  /**
   * Older versions archived permanently failed uploads, which then counted
   * toward the SDK's eviction limit. They carry no reliable error
   * classification, so give each one real upload attempt rather than labelling
   * it a failure. Runs once per database, after a completed sync so a missing
   * photos row means a deleted photo rather than one not yet downloaded. Rows
   * it skips keep their bytes and are restored by the SDK when watched.
   */
  private async restoreLegacyFailures(): Promise<void> {
    await this.withAttachmentContext(async (context) => {
      if (!context.db.currentStatus?.hasSynced) return
      await context.db.writeTransaction(async (tx) => {
        const done = await tx.getOptional(
          `SELECT id FROM attachment_queue_meta WHERE id = ?`,
          [LEGACY_RESTORATION_MARKER],
        )
        if (done) return
        await tx.execute(
          `UPDATE attachments SET state = ?
           WHERE state = ? AND has_synced = 0
           AND local_uri IS NOT NULL AND local_uri != ''
           AND EXISTS (SELECT 1 FROM photos p WHERE p.id = attachments.id)
           AND NOT EXISTS (
             SELECT 1 FROM attachment_failures f WHERE f.id = attachments.id
           )`,
          [AttachmentState.QUEUED_UPLOAD, AttachmentState.ARCHIVED],
        )
        await tx.execute(
          `INSERT OR IGNORE INTO attachment_queue_meta (id, value) VALUES (?, ?)`,
          [LEGACY_RESTORATION_MARKER, new Date().toISOString()],
        )
      })
    })
  }

  override async syncStorage(): Promise<void> {
    await this.withAttachmentContext((context) =>
      requeueMissingDownloads(context.db, context.tableName),
    )
    await super.syncStorage()
    try {
      await this.expireCache()
    } catch (error) {
      // SDK timer callbacks don't catch rejected promises. Cleanup failure must
      // not escape that callback or interfere with the next upload pass.
      console.warn("[Attachments] Cache cleanup deferred:", error)
    }
  }

  override async expireCache(): Promise<void> {
    await this.withAttachmentContext(async (context) => {
      await context.db.execute(
        `DELETE FROM attachment_failures
         WHERE NOT EXISTS (SELECT 1 FROM attachments a WHERE a.id = attachment_failures.id)`,
      )
      // Automatic-retry marks end with the upload: drop them once the photo
      // has synced or its attachment is gone.
      await context.db.execute(
        `DELETE FROM attachment_queue_meta
         WHERE substr(id, 1, ?) = ?
         AND NOT EXISTS (
           SELECT 1 FROM attachments a
           WHERE a.id = substr(attachment_queue_meta.id, ?) AND a.has_synced = 0
         )`,
        [
          AUTOMATIC_RETRY_MARKER_PREFIX.length,
          AUTOMATIC_RETRY_MARKER_PREFIX,
          AUTOMATIC_RETRY_MARKER_PREFIX.length + 1,
        ],
      )
      const expired = await context.db.getAll<{
        id: string
        local_uri: string | null
      }>(
        `SELECT id, local_uri FROM attachments
         WHERE state = ? AND (has_synced = 1 OR local_uri IS NULL OR local_uri = '')
         ORDER BY timestamp DESC, id DESC
         LIMIT -1 OFFSET ?`,
        [AttachmentState.ARCHIVED, this.syncedArchiveLimit],
      )
      for (const attachment of expired) {
        try {
          if (attachment.local_uri) {
            await this.localStorage.deleteFile(attachment.local_uri)
          }
          await context.deleteAttachment(attachment.id)
        } catch (error) {
          // Retain only this row for retry; continue reclaiming the rest.
          console.warn(
            `[Attachments] Cache deletion deferred for ${attachment.id}:`,
            error,
          )
        }
      }
    })
  }
}
